import { useLarguraAbaixoDe } from '@/shared/hooks/useLarguraAbaixoDe';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { TicketsScope } from './api';
import { InboxPanel } from './components/InboxPanel';
import { NewActiveOutboundModal } from './components/NewActiveOutboundModal';
import { NovidadesPanel } from './components/NovidadesPanel';
import { ThreadPanel } from './components/ThreadPanel';
import { useRealtimeListeners } from './hooks';
import { invalidateServerTruthLists, patchTicket } from './inbox-debug';
import { useOpenTicketStore } from './stores/open-ticket-store';
import type { TicketStatus } from './types';

/** Abaixo desta largura, lista e conversa não cabem lado a lado: lista (360) +
 *  o mínimo da conversa (~520) ≈ 880 → 900. (O menu saiu da lateral e foi
 *  pro topo — antes somava +64 do rail.) */
const LARGURA_UMA_COLUNA = 900;

/**
 * Tela principal do Atendimento (Stories 3.4–3.12).
 *
 * Layout (`1-atendimento-v2.html`):
 * Rail (já no AppShell) | Inbox 360px | Thread flex-1
 *
 * Janela estreita (< 960px): uma coisa por vez, como o WhatsApp — sem conversa
 * aberta, a lista ocupa a tela; com conversa, a conversa ocupa a tela e tem a
 * setinha de voltar. A lista fica MONTADA (só escondida) pra não perder
 * rolagem, filtros e busca ao voltar.
 *
 * Real-time: useRealtimeListeners assina rooms do Socket.IO e atualiza queries.
 */
export function AtendimentoPage() {
  const [activeStatus, setActiveStatus] = useState<TicketStatus>('active');
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [newActiveOpen, setNewActiveOpen] = useState(false);
  // Escopo da listagem em "Em atendimento": 'mine' (default — só os do user
  // logado) ou 'all' (admin-only — todas do tenant). Local na aba: F5 reseta.
  const [scope, setScope] = useState<TicketsScope>('mine');
  // user_id selecionado no FilterPopover (admin-only) — quando scope='all',
  // pode filtrar por user específico. null = ver de todos os users.
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  // Idem, por departamento. null = todos os departamentos.
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  // Idem, por canal (todos os perfis, nas 3 abas). null = todos os canais.
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null);
  // Filtros de triagem (todos os perfis) — locais na aba, F5 reseta.
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [onlyUnanswered, setOnlyUnanswered] = useState(false);
  const qc = useQueryClient();
  const umaColuna = useLarguraAbaixoDe(LARGURA_UMA_COLUNA);
  const mostrandoConversa = umaColuna && selectedTicketId !== null;

  // 3º arg: fecha a conversa aberta se outro atendente assumir/receber este
  // ticket (o hook só dispara pra atendente, não pra admin observando).
  useRealtimeListeners(activeStatus, selectedTicketId, () => setSelectedTicketId(null));

  // Espelha o ticket aberto pra store global — hook de notificações lê pra
  // não notificar mensagens da conversa que o atendente já está vendo.
  // Atualização síncrona (não via useEffect) pra eliminar a janela em que
  // selectedTicketId mudou mas openTicketId ainda estava null/stale.
  const setOpenTicketId = useOpenTicketStore((s) => s.setOpenTicketId);

  // Limpa ao desmontar a página (navegar pra outra rota).
  useEffect(() => () => setOpenTicketId(null), [setOpenTicketId]);

  function handleSelectTicket(id: number) {
    setSelectedTicketId(id);
    setOpenTicketId(id);
    // Quem chama `markTicketRead` é o ThreadPanel, num efeito ligado ao
    // ticketId. Aqui NÃO se chama de novo: os dois disparavam no mesmo clique e
    // o `/read` saía DUAS vezes — apareceu no rastro de ações, em todo ticket
    // aberto. O dono ficou com o ThreadPanel porque ele cobre mais caminhos
    // (abrir por link direto, restaurar depois de F5), e não só o clique.
    //
    // O que sobra aqui é o efeito LOCAL do clique, que não depende da resposta
    // do servidor e por isso não precisa mais esperar por ela:
    //
    // Abrir uma conversa zera o não-lido DELA — não é motivo pra rebuscar as
    // 3 listas inteiras do servidor (medido: 48% de todas as buscas de lista
    // vinham daqui, e cada busca custa ~12 queries no Postgres). Atualiza só
    // o card clicado, no cache.
    //
    // Exceção: com o filtro "só não lidas" ligado, o ticket lido tem que
    // SAIR da lista — isso o servidor decide, então aí mantém o refetch.
    if (onlyUnread) {
      // Motor (Fase 2): rebusca SÓ as visões server-truth (a filtrada está
      // entre elas); as listas normais recebem o patch abaixo.
      invalidateServerTruthLists(qc, 'handleSelectTicket');
    }
    patchTicket(qc, id, (t) => (t.unread_count === 0 ? t : { ...t, unread_count: 0 }), {
      origin: 'handleSelectTicket',
      event: 'click',
    });
  }

  /**
   * Chamado quando um ticket é encerrado/transferido/enfileirado (ações que
   * tiram do controle do atendente). `closedTicketId` opcional: quando o close
   * vem do InboxPanel (right-click no card), o ticket fechado pode NÃO ser o
   * que está aberto no thread — só limpa se bater. Quando vem do ThreadPanel,
   * é sempre o ticket aberto (pode passar undefined ou o próprio id).
   */
  function handleTicketClosed(closedTicketId?: number) {
    if (closedTicketId !== undefined && closedTicketId !== selectedTicketId) return;
    setSelectedTicketId(null);
    setOpenTicketId(null);
  }

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden bg-n-50">
      <InboxPanel
        activeStatus={activeStatus}
        onStatusChange={(s) => {
          setActiveStatus(s);
        }}
        selectedTicketId={selectedTicketId}
        onSelectTicket={handleSelectTicket}
        onTicketClosed={handleTicketClosed}
        onNewActive={() => setNewActiveOpen(true)}
        scope={scope}
        onScopeChange={setScope}
        selectedUserId={selectedUserId}
        onSelectedUserIdChange={setSelectedUserId}
        selectedDepartmentId={selectedDepartmentId}
        onSelectedDepartmentIdChange={setSelectedDepartmentId}
        selectedChannelId={selectedChannelId}
        onSelectedChannelIdChange={setSelectedChannelId}
        onlyUnread={onlyUnread}
        onOnlyUnreadChange={setOnlyUnread}
        onlyUnanswered={onlyUnanswered}
        onOnlyUnansweredChange={setOnlyUnanswered}
        larguraTotal={umaColuna}
        oculto={mostrandoConversa}
      />
      {(!umaColuna || selectedTicketId !== null) && (
        <ThreadPanel
          ticketId={selectedTicketId}
          onTicketClosed={handleTicketClosed}
          {...(umaColuna && { onVoltar: () => handleTicketClosed() })}
        />
      )}
      {/* Painel lateral direito (placeholder "Em breve novidades!"). Só em
          telas largas — em uma-coluna a conversa ocupa a tela toda. */}
      {!umaColuna && <NovidadesPanel />}
      <NewActiveOutboundModal
        open={newActiveOpen}
        onClose={() => setNewActiveOpen(false)}
        onCreated={(ticketId) => {
          setNewActiveOpen(false);
          setActiveStatus('active');
          setSelectedTicketId(ticketId);
          setOpenTicketId(ticketId);
        }}
      />
    </div>
  );
}
